import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { 
  loadStripe, 
  Stripe, 
  StripeElements, 
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeCardCvcElement,
  StripeCardNumberElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardCvcElementChangeEvent,
} from '@stripe/stripe-js';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { FileConversionService } from '../services/file-conversion.service';
import { PaymentService } from '../services/payment.service';
import { DataSharingService } from '../services/data-sharing.service';
import { ProcessApiService } from '../services/process.api';

@Component({
  selector: 'app-checkout',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, HeaderComponent, FooterComponent],
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.scss'
})
export class CheckoutComponent implements OnInit, OnDestroy {
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private cardNumber: StripeCardNumberElement | null = null;
  private cardExpiry: StripeCardExpiryElement | null = null;
  private cardCvc: StripeCardCvcElement | null = null;

  filename: string = '';
  email: string = '';

  // Form data
  termsAccepted: boolean = false;
  isProcessing: boolean = false;
  errorMessage: string = '';
  isLoadingStripe: boolean = true;
  paymentSuccess: boolean = false;

  // Payment configuration
  readonly PAYMENT_AMOUNT = 50; // 0.50€ in cents
  readonly PAYMENT_CURRENCY = 'EUR';

  // Stripe configuration (replace with your actual publishable key)
  private readonly STRIPE_PUBLISHABLE_KEY = 'pk_live_51RutlMClDxXzG7JXtEv4PQyROiIsdogSBn4ktFAyk3uAtigSH3GqpZdc7WgOcQbPs6SOPuXObE3XvMyzXpohViFU00EnFii4rm';

  constructor(
    private fileConversionService: FileConversionService,
    private paymentService: PaymentService,
    private dataSharingService: DataSharingService,
    private processApiService: ProcessApiService,
    private router: Router,
    private meta: Meta,
    private title: Title
  ) {}

  ngOnInit(): void {
    // Définir le titre de la page (40 caractères - optimal)
    this.title.setTitle('Paiement sécurisé – pdf-to-word.fr');
    
    // Définir les meta tags SEO (160 caractères - optimal)
    this.meta.updateTag({ name: 'description', content: 'Finalisez votre paiement pour convertir votre PDF en Word. Paiement sécurisé par Stripe. Accès 24h pour 0,50€ ou 14 jours pour 19,80€.' });
    this.meta.updateTag({ name: 'keywords', content: 'paiement conversion PDF, checkout pdf-to-word, paiement sécurisé Stripe, achat conversion PDF Word, tarif conversion PDF' });
    
    // Meta tags supplémentaires pour le SEO
    this.meta.updateTag({ property: 'og:title', content: 'Paiement sécurisé – pdf-to-word.fr' });
    this.meta.updateTag({ property: 'og:description', content: 'Finalisez votre paiement pour convertir votre PDF en Word. Paiement sécurisé par Stripe. Accès 24h pour 0,50€ ou 14 jours pour 19,80€.' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });

    const conversionData = this.fileConversionService.getConversionData();
    if (conversionData && conversionData.filename) {
      this.filename = conversionData.filename.replace(conversionData.originalFormat ?? '.pdf', conversionData.targetFormat ?? '.docx');
    }

    // Récupérer l'email depuis le service de partage de données
    const prospectData = this.dataSharingService.getProspectData();
    if (prospectData && prospectData.email) {
      this.email = prospectData.email;
    }

    this.initializeStripe().then(() => {
      this.isLoadingStripe = false;
    });
  }

  ngOnDestroy(): void {
    // Clean up Stripe elements
    if (this.cardNumber) {
      this.cardNumber.destroy();
    }
    if (this.cardExpiry) {
      this.cardExpiry.destroy();
    }
    if (this.cardCvc) {
      this.cardCvc.destroy();
    }
  }

  private async initializeStripe(): Promise<void> {
    try {
      this.stripe = await loadStripe(this.STRIPE_PUBLISHABLE_KEY);
      if (this.stripe) {
        this.elements = this.stripe.elements();
        this.setupStripeElements();
      }
    } catch (error) {
      console.error('Error loading Stripe:', error);
      this.errorMessage = 'Erreur lors du chargement du système de paiement';
      this.isLoadingStripe = false;
    }
  }

  private setupStripeElements(): void {
    if (!this.elements) {
      this.isLoadingStripe = false;
      return;
    }

    // Card number element
    this.cardNumber = this.elements.create('cardNumber', {
      style: {
        base: {
          fontSize: '16px',
          color: '#333',
          fontFamily: 'Roboto, sans-serif',
          '::placeholder': {
            color: '#818386',
          },
        },
      },
      placeholder: '1234 1234 1234 1234',
    });
    this.cardNumber.mount('#card-number');

    // Card expiry element
    this.cardExpiry = this.elements.create('cardExpiry', {
      style: {
        base: {
          fontSize: '16px',
          color: '#333',
          fontFamily: 'Roboto, sans-serif',
          '::placeholder': {
            color: '#818386',
          },
        },
      },
      placeholder: 'MM / AA',
    });
    this.cardExpiry.mount('#card-expiry');

    // Card CVC element
    this.cardCvc = this.elements.create('cardCvc', {
      style: {
        base: {
          fontSize: '16px',
          color: '#333',
          fontFamily: 'Roboto, sans-serif',
          '::placeholder': {
            color: '#818386',
          },
        },
      },
      placeholder: 'CVC',
    });
    this.cardCvc.mount('#card-cvc');

    // Handle real-time validation errors
    this.setupValidationHandlers();
  }

  private setupValidationHandlers(): void {
    if (this.cardNumber) {
      this.cardNumber.on('change', (event: StripeCardNumberElementChangeEvent) => {
        this.handleStripeElementChange(event, 'cardNumber');
      });
    }

    if (this.cardExpiry) {
      this.cardExpiry.on('change', (event: StripeCardExpiryElementChangeEvent) => {
        this.handleStripeElementChange(event, 'cardExpiry');
      });
    }

    if (this.cardCvc) {
      this.cardCvc.on('change', (event: StripeCardCvcElementChangeEvent) => {
        this.handleStripeElementChange(event, 'cardCvc');
      });
    }
  }

  private handleStripeElementChange(event: any, elementType: string): void {
    // Map element types to actual DOM IDs
    const elementIdMap: { [key: string]: string } = {
      'cardNumber': 'card-number',
      'cardExpiry': 'card-expiry', 
      'cardCvc': 'card-cvc'
    };
    
    const elementId = elementIdMap[elementType];
    if (!elementId) return;
    
    const element = document.querySelector(`#${elementId}`) as HTMLElement;
    if (!element) return;
    
    if (event.error) {
      element.classList.add('StripeElement--invalid');
      element.classList.remove('StripeElement--complete');
    } else if (event.complete) {
      element.classList.add('StripeElement--complete');
      element.classList.remove('StripeElement--invalid');
    } else {
      element.classList.remove('StripeElement--invalid', 'StripeElement--complete');
    }
  }

  onTermsChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.termsAccepted = checkbox.checked;
  }

  async onSubmit(event: Event): Promise<void> {
    event.preventDefault();
    
    if (!this.stripe || !this.elements || !this.termsAccepted) {
      this.errorMessage = 'Veuillez accepter les conditions générales de vente';
      return;
    }

    this.isProcessing = true;
    this.errorMessage = '';

    try {
      // Etape 1 : Créer un customer Stripe
      const customer = await firstValueFrom(this.paymentService.createCustomer({
        email: this.email,
        id_site: 6,
        metadata: {
          filename: this.filename,
          email: this.email,
          product: 'PDF to Word conversion'
        }
      }));
      console.log('Customer stripe created');
      
      // Etape 2 : Créer un payment method
      if (!this.stripe) throw new Error('Stripe not initialized');
      const { error: paymentMethodError, paymentMethod } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardNumber!,
        billing_details: {
          email: this.email,
        },
      });

      if (paymentMethodError) throw new Error(paymentMethodError.message);
      if (!paymentMethod) throw new Error('Failed to create payment method');
      console.log('Payment method created');

      // Etape 3 : Créer un payment intent
      const paymentIntent = await firstValueFrom(this.paymentService.createPaymentIntent({
        amount: this.PAYMENT_AMOUNT,
        currency: this.PAYMENT_CURRENCY,
        payment_method_id: paymentMethod.id,
        customer_id: customer.customer_id,
        id_site: 6
      }));
      console.log('Payment intent created');

      // Etape 4 : Confirmer le paiement avec Stripe
      const confirmation = await firstValueFrom(this.paymentService.confirmPayment({
        payment_intent_id: paymentIntent.payment_intent_id,
        payment_method_id: paymentMethod.id,
        customer_id: customer.customer_id,
        id_site: 6
      }));
      console.log('Payment confirmed');

      // Déclenchement des APIs après paiement réussi
      if (confirmation.status === 'succeeded') {
        // Envoyer l'ID de transaction à GTM
        if (typeof window !== 'undefined' && (window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: 'purchase',
            id_transaction: paymentIntent.payment_intent_id
          });
        }

        // Etape 5 : Créer une subscription Stripe si Paiement réussi
        const subscription = await firstValueFrom(this.paymentService.createSubscription({
          customer_id: customer.customer_id,
          id_site: 6
        }));
        console.log('Subscription created');

        // Etape 6 : Créer un client
        const clientResponse = await firstValueFrom(this.processApiService.createClient({
          id_site: 6,
          prospect_id: this.dataSharingService.getProspectData()?.id ?? 0,
          payment_status: 'SUCCESS',
          
          // Information de la transaction
          transaction: {
            reference: paymentIntent.payment_intent_id ?? '',
            montant_ht: this.PAYMENT_AMOUNT,
            montant_ttc: this.PAYMENT_AMOUNT,
            moyen_paiement: 'Stripe',
            paymentIntent: paymentIntent.payment_intent_id ?? '',
          },
          card: {
            cardToken: paymentMethod.id,
            cardHolder: this.email,
            cardLast4: paymentMethod.card?.last4 ?? '',
            cardExpiry: paymentMethod.card?.exp_month + '/' + paymentMethod.card?.exp_year,
            cardType: paymentMethod.card?.brand ?? '',
          },
          product: this.filename
        }));
        console.log('Local client created');

        // Mettre à jour le clientId dans le service de partage de données
        this.dataSharingService.setClientId(clientResponse.data.client.id);

        // Etape 7 : Créer un subscription
        const subscriptionResponse = await firstValueFrom(this.processApiService.createSubscription({
          id_site: 6,
          id_client: clientResponse.data.client.id,
          origine: 'pdf-to-word.fr',
          id_abonnement_payment_method: subscription.subscription_id ?? '',
          id_transaction: paymentIntent.payment_intent_id ?? '',
          id_produit: 1,
          abonneData: {
            email: this.email,
          },
        }));
        console.log('Subscription local created');

        // Etape 8 : Créer un payment method local
        const paymentMethodLocalResponse = await firstValueFrom(this.processApiService.createPaymentMethod({
          customer_id: customer.customer_id ?? '',
          client_id: clientResponse.data.client.id,
          payment_gateway: 'STRIPE',
        }));
        console.log('Payment method local created');

        this.isProcessing = false;
        this.router.navigate(['/success']);
      }

    } catch (error) {
      console.error('Payment error:', error);
      this.errorMessage = 'Erreur lors du traitement du paiement';
      this.isProcessing = false;
    }
  }

  // Helper method to check if form is valid
  isFormValid(): boolean {
    return this.termsAccepted && !this.isProcessing;
  }

  // Get formatted amount for display
  getFormattedAmount(): string {
    return (this.PAYMENT_AMOUNT / 100).toFixed(2);
  }
}
